import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Shield, AlertCircle, CheckCircle, Home, Hash, TrendingDown, Clock, X } from 'lucide-react';

interface InsuranceRequest {
  id: string;
  name: string;
  email: string;
  status: string;
  created_at: string;
  car_make: string;
  car_model: string;
  car_value: number;
  registration: string;
  bid_stats?: {
    lowest_bid: number | null;
    bid_count: number;
    unique_insurers: number;
  };
}

interface BidHistory {
  amount: number;
  created_at: string;
  insurer: {
    name: string;
  };
}

interface OriginalAdminViewProps {
  onExit: () => void;
}

export function OriginalAdminView({ onExit }: OriginalAdminViewProps) {
  const [requests, setRequests] = useState<InsuranceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<string | null>(null);
  const [bidHistory, setBidHistory] = useState<BidHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [pendingInsurers, setPendingInsurers] = useState<any[]>([]);
  const [showPendingInsurers, setShowPendingInsurers] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState<'all' | 'customer' | 'email' | 'reference' | 'registration'>('all');

  useEffect(() => {
    fetchRequests();
    fetchPendingInsurers();
  }, []);

  async function fetchPendingInsurers() {
    try {
      const { data, error } = await supabase
        .from('insurers')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPendingInsurers(data || []);
    } catch (error) {
      console.error('Error fetching pending insurers:', error);
    }
  }

  async function handleInsurerApproval(insurerId: string, approve: boolean) {
    try {
      const { error } = await supabase.rpc(
        'update_insurer_status',
        {
          p_insurer_id: insurerId,
          p_status: approve ? 'active' : 'rejected'
        }
      );

      if (error) throw error;
      await fetchPendingInsurers();
    } catch (error) {
      console.error('Error updating insurer status:', error);
    }
  }

  async function fetchBidHistory(requestId: string) {
    setLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('bids')
        .select(`
          amount,
          status,
          created_at,
          insurers!inner (
            name
          )
        `)
        .eq('request_id', requestId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBidHistory(data || []);
    } catch (error) {
      console.error('Error fetching bid history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }

  async function fetchRequests() {
    try {
      const { data, error } = await supabase 
        .from('insurance_requests') 
        .select(`
          id,
          reference_number,
          name,
          email,
          status,
          created_at,
          car_make,
          car_model,
          car_value,
          registration,
          bids!left (
            id,
            amount,
            status,
            insurer_id,
            created_at,
            insurers!inner (
              id,
              name
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const processedData = (data || []).map(request => {
        const allBids = request.bids || [];
        const activeBids = allBids.filter(bid => bid.status === 'active');
        const uniqueInsurers = new Set(allBids.map(bid => bid.insurer_id));
        
        return {
          ...request,
          bid_stats: {
            lowest_bid: activeBids.length > 0 ? Math.min(...activeBids.map(bid => bid.amount)) : null,
            bid_count: allBids.length,
            unique_insurers: uniqueInsurers.size
          },
          bids: undefined
        };
      });

      setRequests(processedData);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setError('Failed to load insurance requests');
    } finally {
      setLoading(false);
    }
  }

  const filteredRequests = requests.filter(request => {
    if (!searchTerm) return true;
    
    const term = searchTerm.toLowerCase();
    
    switch (searchField) {
      case 'customer':
        return request.name.toLowerCase().includes(term);
      case 'email':
        return request.email.toLowerCase().includes(term);
      case 'reference':
        return request.reference_number?.toString().includes(term);
      case 'registration':
        return request.registration.toLowerCase().includes(term);
      case 'all':
        return request.name.toLowerCase().includes(term) ||
               request.email.toLowerCase().includes(term) ||
               request.reference_number?.toString().includes(term) ||
               request.registration.toLowerCase().includes(term);
      default:
        return true;
    }
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading requests...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center text-red-700">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Shield className="h-8 w-8 text-indigo-600" />
          <h1 className="text-2xl font-bold text-gray-900">Original Admin View</h1>
        </div>
        <div className="flex items-center space-x-6">
          <div className="flex items-center text-gray-600">
            <Shield className="h-5 w-5 text-green-500 mr-2" />
            <span>Total Requests: {requests.length}</span>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative flex items-center">
              <select
                value={searchField}
                onChange={(e) => setSearchField(e.target.value as any)}
                className="border rounded-l-md px-3 py-2 text-sm"
              >
                <option value="all">All Fields</option>
                <option value="customer">Customer</option>
                <option value="email">Email</option>
                <option value="reference">Reference #</option>
                <option value="registration">Number Plate</option>
              </select>
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-l-0 border rounded-r-md px-3 py-2 w-64 text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          {pendingInsurers.length > 0 && (
            <div className="flex items-center text-gray-600">
              <button
                onClick={() => setShowPendingInsurers(true)}
                className="flex items-center px-3 py-1.5 bg-yellow-100 text-yellow-800 rounded-md hover:bg-yellow-200"
              >
                <Shield className="h-5 w-5 mr-2" />
                Pending Insurers ({pendingInsurers.length})
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer / Reference
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Vehicle
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Bid Stats
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredRequests.map((request) => (
              <tr key={request.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{request.name}</div>
                  <div className="text-sm text-gray-500">{request.email}</div>
                  <div className="text-xs font-mono text-gray-400 mt-1">Reference #{request.reference_number}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {request.car_make} {request.car_model}
                    <div className="text-sm text-gray-500">
                      Value: £{request.car_value?.toLocaleString('en-GB')}
                    </div>
                    <div className="text-sm text-gray-500 font-mono">
                      {request.registration}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => {
                      setSelectedRequest(request.id);
                      fetchBidHistory(request.id);
                    }}
                    className="text-left hover:bg-gray-50 p-2 rounded-md -m-2 w-full"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center text-sm text-gray-900">
                        <TrendingDown className="w-4 h-4 mr-2 text-green-600" />
                        {request.bid_stats?.lowest_bid
                          ? `£${request.bid_stats.lowest_bid.toLocaleString('en-GB', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}` 
                          : 'No bids'}
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <Hash className="w-4 h-4 mr-2" />
                        {request.bid_stats?.bid_count || 0} bid{request.bid_stats?.bid_count !== 1 ? 's' : ''} from {request.bid_stats?.unique_insurers || 0} insurer{request.bid_stats?.unique_insurers !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    request.status === 'new' ? 'bg-blue-100 text-blue-800' :
                    request.status === 'bidding' ? 'bg-yellow-100 text-yellow-800' :
                    request.status === 'expired' ? 'bg-red-100 text-red-800' :
                    request.status === 'bidding_complete' ? 'bg-green-100 text-green-800' :
                    request.status === 'awaiting_insurer' ? 'bg-purple-100 text-purple-800' :
                    request.status === 'complete' ? 'bg-indigo-100 text-indigo-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {request.status === 'new' ? <AlertCircle className="w-4 h-4 mr-1" /> :
                     request.status === 'bidding' ? <Clock className="w-4 h-4 mr-1" /> :
                     request.status === 'expired' ? <AlertCircle className="w-4 h-4 mr-1" /> :
                     request.status === 'bidding_complete' ? <CheckCircle className="w-4 h-4 mr-1" /> :
                     request.status === 'awaiting_insurer' ? <Clock className="w-4 h-4 mr-1" /> :
                     request.status === 'complete' ? <CheckCircle className="w-4 h-4 mr-1" /> :
                     <Shield className="w-4 h-4 mr-1" />}
                    {request.status.replace(/_/g, ' ')}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(request.created_at).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bid History Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Bid History</h3>
              <button
                onClick={() => setSelectedRequest(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {loadingHistory ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : bidHistory.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No bids have been placed yet.</p>
            ) : (
              <div className="space-y-4">
                {bidHistory.map((bid, index) => (
                  <div
                    key={index} 
                    className={`flex justify-between items-center p-2 rounded ${bid.status === 'active' ? 'bg-green-50 border border-green-100' : 'bg-gray-50 border border-gray-100'}`}>
                    <div>
                      <p className="font-medium text-gray-900">
                        {bid.insurers?.name || 'Unknown Insurer'} 
                        {bid.status === 'withdrawn' && (
                          <span className="ml-2 text-sm text-gray-500">(Withdrawn)</span>
                        )}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(bid.created_at).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className={`text-lg font-medium ${
                      bid.status === 'active' ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      £{bid.amount.toLocaleString('en-GB', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pending Insurers Modal */}
      {showPendingInsurers && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Pending Insurer Approvals</h3>
              <button
                onClick={() => setShowPendingInsurers(false)}
                className="text-gray-400 hover:text-gray-500"
                aria-label="Close"
              >
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            
            {pendingInsurers.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No pending insurer approvals</p>
            ) : (
              <div className="space-y-4">
                {pendingInsurers.map((insurer) => (
                  <div key={insurer.id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium">{insurer.name}</h4>
                        <p className="text-sm text-gray-500">Applied: {new Date(insurer.created_at).toLocaleString()}</p>
                      </div>
                      <div className="space-x-2">
                        <button
                          onClick={() => handleInsurerApproval(insurer.id, true)}
                          className="px-3 py-1.5 bg-green-100 text-green-800 rounded hover:bg-green-200"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleInsurerApproval(insurer.id, false)}
                          className="px-3 py-1.5 bg-red-100 text-red-800 rounded hover:bg-red-200"
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}